<template>
  <div id="backtester-advanced-options-tab">
    <v-container fluid pa-0>
      <v-row>
        <!-- Hedging strategy -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header class="d-inline-flex align-center">
            <v-chip
              v-if="!accessAllowed({ requires_pro_subscription: true })"
              color="accent text-white"
              class="v-chip--x-small font-weight-bold ma-0 mr-2"
            >
              PRO
            </v-chip>
            Hedging strategy
            <v-btn
              variant="plain"
              :icon="mdiInformation"
              class="mx-0 mt-0"
              @click="hedgingStrategyHelpDialog = true"
            />
          </helpers-section-sub-header>

          <!-- Choose a hedging strategy -->
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="2">
              <v-select
                v-model="backtesterCriteria.hedging_type"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                :items="hedgingItems"
                item-title="text"
                label="Choose a hedging strategy"
                hide-details
                attach
                class="pt-0"
              />
            </v-col>
          </v-row>

          <!-- Hedging strategy: Options -->
          <v-row>
            <!-- Moving Average Target -->
            <v-col v-if="backtesterCriteria.hedging_type === 2" cols="12" sm="6" md="4" xl="2">
              <v-select
                v-model="backtesterCriteria.hedge_moving_average_target"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                :items="hedgingTargetsItems"
                item-title="text"
                label="Moving Average Target"
                hide-details
                attach
              />
            </v-col>

            <!-- Hedge Investment -->
            <v-col v-if="backtesterCriteria.hedging_type === 2" cols="12" sm="6" md="4" xl="2">
              <v-select
                v-model="backtesterCriteria.hedging_investment"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                :items="hedgeInvestmentsItems"
                item-title="description"
                item-value="id"
                attach
                label="Hedge Investment"
                hide-details
              />
            </v-col>

            <!-- Moving Average Months -->
            <v-col v-if="backtesterCriteria.hedging_type === 2" cols="12" sm="6" md="4" xl="2">
              <v-text-field
                v-model.number="backtesterCriteria.hedging_moving_average_months"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                type="number"
                label="Moving Average Months"
                hide-details
              />
            </v-col>

            <!-- Hedging % -->
            <v-col
              v-if="backtesterCriteria.hedging_type === 1 || backtesterCriteria.hedging_type === 2"
              cols="12"
              sm="6"
              md="4"
              xl="2"
            >
              <v-select
                v-model="backtesterCriteria.hedging_percentage"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                :items="hedgingPercentageItems"
                item-title="text"
                label="Hedging %"
                suffix="%"
                attach
                hide-details
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Pick stocks using Double-Down strategies -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header>
            Pick stocks using Double-Down strategies
            <v-btn
              variant="plain"
              :icon="mdiInformation"
              class="mx-0 mt-0"
              @click="doubleDownStrategyHelpDialog = true"
            />
          </helpers-section-sub-header>

          <v-row>
            <!-- Double-Down: ON/OFF switch -->
            <v-col cols="12" pt-0>
              <v-switch
                v-model="backtesterCriteria.double_down.selected"
                color="primary"
                label="Enable Double-Down Selection"
                hide-details
                class="mt-0"
              />
            </v-col>

            <!-- Double-Down: Options -->
            <template v-if="backtesterCriteria.double_down.selected">
              <!-- % increase in shares purchased -->
              <v-col cols="12" sm="6" md="4" xl="3">
                <v-text-field
                  v-model.number="backtesterCriteria.double_down.purchase_percent"
                  type="number"
                  suffix="%"
                  label="% increase in shares purchased"
                  hint="Only look at stocks where the manager has increased shares X%"
                />
              </v-col>

              <!-- Price Trigger: % decline in stock price -->
              <v-col cols="12" sm="6" md="4" xl="3">
                <v-text-field
                  v-model.number="backtesterCriteria.double_down.percent_decline"
                  type="number"
                  suffix="%"
                  label="Price Trigger: % decline in stock price"
                  hint="Only look at stocks down at least X%. Backtester compares the current end of quarter price to the average price paid by the manager for the stock"
                />
              </v-col>

              <!-- % of funds portfolio -->
              <v-col cols="12" sm="6" md="4" xl="3">
                <v-text-field
                  v-model.number="backtesterCriteria.double_down.percent_of_portfolio"
                  type="number"
                  label="% of funds portfolio"
                  suffix="%"
                  hint="Only look at stocks that are at least X% of the funds portfolio"
                />
              </v-col>

              <v-col v-show="$vuetify.display.sm || $vuetify.display.xl" sm="6" xl="3" />

              <!-- Price Trigger: OR decline in stock price as measured in Standard Deviations -->
              <v-col cols="12" sm="6" md="4" xl="3">
                <label class="text-caption text-grey"
                  >Price Trigger: OR decline in stock price as measured in Std Dev</label
                >
                <v-text-field
                  v-model.number="backtesterCriteria.double_down.stdev_decline"
                  type="number"
                  hint="Only look at stocks that have declined by 1 or more standard deviations (taken from the past year) from the average price paid by the manager"
                  class="pt-0"
                />
              </v-col>

              <!-- Price Trigger: OR % increase in stock price from previous quarter -->
              <v-col cols="12" sm="6" md="4" xl="3">
                <label class="text-caption text-grey"
                  >Price Trigger: OR % increase in stock price from previous quarter</label
                >
                <v-text-field
                  v-model.number="backtesterCriteria.double_down.percent_increase"
                  type="number"
                  suffix="%"
                  hint="Only look at stocks that have increased in price. Backtester compares the current end of quarter price to the previous quarter's end of quarter price"
                  class="pt-0"
                />
              </v-col>

              <!-- Must have 13D/G Position -->
              <v-col cols="12">
                <v-checkbox
                  v-model="backtesterCriteria.double_down.check_for_13d"
                  label="Must have 13D/G position"
                  hint="If checked then search will return stocks that have a 13D/G filing made by the manager who had a pre-existing position that is at a loss based on their prior 13Fs"
                  persistent-hint
                  class="mt-0"
                />
              </v-col>
            </template>
          </v-row>
        </v-col>

        <!-- Stock sell buffer -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header class="d-inline-flex align-center">
            <v-chip
              v-if="!accessAllowed({ requires_pro_subscription: true })"
              color="accent text-white"
              class="v-chip--x-small font-weight-bold ma-0 mr-2"
            >
              PRO
            </v-chip>
            Stock sell buffer
            <v-btn
              variant="plain"
              :icon="mdiInformation"
              class="mx-0 mt-0"
              @click="stockSellBufferHelpDialog = true"
            />
          </helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="2">
              <v-select
                v-model="backtesterCriteria.sell_buffer_threshold"
                :disabled="!accessAllowed({ requires_pro_subscription: true })"
                :items="sellBufferItems"
                item-title="text"
                item-disabled="disabled"
                label="Sell buffer threshold"
                hide-details
                attach
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Stop loss/gain exit strategies -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header>
            Stop loss/gain exit strategies
            <sup class="text-accent ml-1">BETA</sup>
          </helpers-section-sub-header>

          <v-row>
            <v-col cols="12">
              You can optionally include rules for exiting holdings when certain loss or gain
              thresholds are reached.
            </v-col>

            <!-- Stop loss/gain exit strategies: ON/OFF switch -->
            <v-col cols="12" pt-0>
              <v-switch
                v-model="backtesterCriteria.stop_loss_enabled"
                :disabled="
                  backtesterCriteria.stock_selection_group === 4 ||
                  backtesterCriteria.stock_selection_group === 9
                "
                :hide-details="
                  backtesterCriteria.stock_selection_group !== 4 &&
                  backtesterCriteria.stock_selection_group !== 9
                "
                color="primary"
                label="Enable Stop-Loss Rules"
                hint="Stop loss rules disabled when choosing stocks by taking top stocks from each fund"
                persistent-hint
                class="mt-0"
              />
            </v-col>

            <!-- Stop loss/gain exit strategies: Inputs -->
            <template v-if="backtesterCriteria.stop_loss_enabled">
              <v-col cols="12">
                <label class="text-caption text-grey"
                  >Sell when price has declined a certain percentage from Initial Price Paid</label
                >
                <v-text-field
                  v-model.number="backtesterCriteria.stop_loss_decline_from_purchase"
                  suffix="%"
                  mask="###"
                  hide-details
                  class="pt-0"
                  style="max-width: 100px"
                />
              </v-col>
              <v-col cols="12">
                <label class="text-caption text-grey"
                  >Sell when price has increased a certain percentage from Initial Price Paid</label
                >
                <v-text-field
                  v-model.number="backtesterCriteria.stop_loss_increase_from_purchase"
                  suffix="%"
                  mask="###"
                  hide-details
                  class="pt-0"
                  style="max-width: 100px"
                />
              </v-col>
            </template>
          </v-row>
        </v-col>

        <!-- Rebalancing frequency -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header> Rebalancing frequency </helpers-section-sub-header>

          <v-row>
            <v-col cols="12">
              By default backtester rebalances quarterly using new 13F filing data. You can adjust
              the rebalancing frequency using the dropdown below. Reasons for changing the frequency
              include lower turnover and possible tax advantages by keeping stocks at least one year
              before selling.
            </v-col>

            <v-col cols="12" sm="6" md="4" xl="2">
              <v-select
                v-model="backtesterCriteria.rebalance_frequency"
                :disabled="backtesterCriteria.no_rebalancing"
                :items="rebalanceItems"
                item-title="text"
                label="Rebalance frequency"
                hide-details
                attach
                class="pt-0"
              />
            </v-col>

            <v-col cols="12">
              <p>
                You can also turn off all rebalancing after the initial portfolio is created.
                Backtester may still add stocks if any holdings in the portfolio become delisted.
              </p>
              <v-checkbox
                v-model="backtesterCriteria.no_rebalancing"
                label="Do not rebalance after initial portfolio created"
                hide-details
              />
            </v-col>

            <v-col cols="12">
              <p>
                You may also turn off adjusting any existing holdings in your portfolio each
                quarter. Holdings may still be closed out (when securities fall out of your fund(s)
                holdings or out of the sell buffer for example) each quarter, but holdings carried
                forward each quarter will not be re-adjusted to match the weighting specific in your
                backtest parameters.
              </p>
              <v-checkbox
                v-model="backtesterCriteria.no_weight_adjustment"
                label="Do not adjust existing holdings carried forward"
                hide-details
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Rebalancing date -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header> Rebalancing date </helpers-section-sub-header>

          <v-row>
            <v-col cols="12">
              How many days after the end-of-quarter date before doing the backtest?
            </v-col>
            <v-col cols="12" sm="7" md="5" xl="3">
              <v-row align="center" ma-0>
                <v-col pr-0 class="text-subtitle-1 shrink"> 0 </v-col>
                <v-col>
                  <v-slider
                    v-model="backtestingDaysProxy"
                    :disabled="backtesterCriteria.backtesting_days_submitted"
                    min="0"
                    max="60"
                    thumb-label="always"
                    thumb-size="24"
                    always-dirty
                    hide-details
                    color="primary"
                    class="mt-0"
                    @update:model-value="backtesterCriteria.backtesting_days = backtestingDaysProxy"
                  />
                </v-col>
                <v-col pl-0 class="text-subtitle-1 shrink"> 60 </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" pt-0>
              <v-checkbox
                v-model="backtesterCriteria.rebalance_when_submitted"
                label="Or use day whenever the filing was submitted to the SEC (or the last filing when using a group of funds)"
                hide-details
                class="mt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Time period range -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header> Time period range </helpers-section-sub-header>

          <v-row>
            <v-col cols="12">
              By default backtester will test your strategy using all available dates going back to
              2001 (for subscribers only). You can optionally adjust when the backtest actually
              starts.
            </v-col>

            <!-- Time period range: Inputs -->
            <v-col cols="12" sm="6" md="4" xl="2">
              <v-select
                v-model="backtesterCriteria.earliest_quarter"
                :items="quarters"
                item-title="description"
                item-value="id"
                label="Starting Quarter"
                hide-details
                attach
                class="pt-0"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" xl="2">
              <v-select
                v-model="backtesterCriteria.ending_quarter"
                :items="quarters"
                item-title="description"
                item-value="id"
                label="Ending Quarter"
                hide-details
                attach
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <!-- help dialog: Hedging strategy -->
    <v-dialog v-model="hedgingStrategyHelpDialog" width="600px">
      <v-card>
        <v-card-title class="text-h6 primary text-white"> Hedging Strategy Options </v-card-title>
        <v-card-text>
          <dl>
            <dt>Simple Short Exposure</dt>
            <dd>
              <p>
                This strategy effectively shorts the S&P 500 by buying shares in the S&P 500 Short
                ETF. Simple Hedging will assume that 100% of the backtest's invested principal is
                always in the backtest's long positions.
              </p>
              <p>
                For example, if you choose a 25% hedging rate, then for a $10,000 portfolio, you
                will be long $10,000 and have a $2,500 short on the S&P 500. The short position will
                adjust each rebalancing period to match 25% of the total portfolio. Any gains/losses
                from the short are added to the overall available capital for the long positions in
                the portfolio.
              </p>
            </dd>
            <dt>Moving Average Hedge</dt>
            <dd>
              <p>
                With this strategy, your portfolio will be hedged by shorting the S&P 500 whenever
                your long portfolio's performance drops below the monthly moving average. So if you
                set a 25% hedge, 25% of your long portfolio will be sold and the funds reallocated
                to the short whenever the hedge is in effect. Once your long portfolio performance
                goes back over the moving average, the short hedge will be removed and the funds
                from the short will be reallocated to your long positions.
              </p>
              <p>
                When hedging strategies are being used, backtests can only go back as far as 2006.
                This is the date the S&P 500 Short ETF first appeared.
              </p>
            </dd>
          </dl>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- help dialog: Double-Down strategies -->
    <v-dialog v-model="doubleDownStrategyHelpDialog" width="600px">
      <v-card>
        <v-card-title class="text-h6 primary text-white">
          Double-Down Stock Selection Strategies
        </v-card-title>
        <v-card-text>
          <p>
            Double down strategies allow you to select stocks where the price has declined QoQ (or
            increased), and the manager then increased their existing position in the security,
            showing they still have conviction in the stock.
          </p>
          <p class="mb-0">
            Stocks selected will remain in the portfolio as long as at least one filer in your group
            holds the stock and the percent of the portfolio is at least whatever level you set for
            "% of portfolio" below.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- help dialog: Stock Sell Buffer -->
    <v-dialog v-model="stockSellBufferHelpDialog" width="600px">
      <v-card>
        <v-card-title class="text-h6 primary text-white"> Stock Sell Buffer </v-card-title>
        <v-card-text>
          <p>
            You can optionally include a stock sell buffer in your backtesting model. It works by
            keeping a stock in your portfolio even when it would otherwise drop out during
            rebalancing as long as it remains within your sell buffer threshold.
          </p>
          <p>
            For example, you are backtesting with the top 20 stocks each quarter and set a sell
            buffer of 50. In the first quarter, AAPL is one of the top 20 stocks and is included in
            the portfolio. In quarter 2, AAPL drops down to number 25. Without the sell buffer, AAPL
            would drop out for that quarter, but since it is still within the sell buffer threshold
            of 50, it will remain in the portfolio. Your portfolio size remains the same. The stocks
            that would have fallen out stay in and the stocks that would otherwise have been added
            stay out.
          </p>
          <p class="mb-0">Using the sell buffer may help produce a lower turnover rate.</p>
          <p class="mb-0">
            In order to work, the sell buffer must be greater than the number of holdings.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Modules
import { mapStoreState, mapFields } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'

// Mixins
import AccessMixin from '@/mixins/access'
import { mdiInformation } from '@mdi/js'

export default {
  mixins: [AccessMixin],

  data: () => ({
    mdiInformation,
    backtestingDaysProxy: 46,
    hedgingStrategyHelpDialog: false,
    doubleDownStrategyHelpDialog: false,
    stockSellBufferHelpDialog: false,
    hedgingItems: [
      { text: 'No Hedging', value: 0 },
      { text: 'Short Exposure Hedge', value: 1 },
      { text: 'Moving Average Hedge', value: 2 }
    ],
    hedgingTargetsItems: [
      { text: 'Your backtested portfolio', value: 0 },
      { text: 'S&P 500 TR Index', value: 1 },
      { text: 'Russell 2K TR Index', value: 2 }
    ],
    hedgeInvestmentsItems: [
      { id: 1, description: 'Cash' },
      { id: 2, description: 'S&P 500 Short ETF' }
    ],
    hedgingPercentageItems: [
      { text: '25', value: 25 },
      { text: '50', value: 50 },
      { text: '75', value: 75 },
      { text: '100', value: 100 }
    ],
    sellBufferItems: [
      { text: 'No Sell Buffer', value: 0, disabled: false },
      { text: '5', value: 5, disabled: false },
      { text: '6', value: 6, disabled: false },
      { text: '7', value: 7, disabled: false },
      { text: '10', value: 10, disabled: false },
      { text: '15', value: 15, disabled: false },
      { text: '25', value: 25, disabled: false },
      { text: '30', value: 30, disabled: false },
      { text: '35', value: 35, disabled: false },
      { text: '50', value: 50, disabled: false },
      { text: '100', value: 100, disabled: false }
    ],
    rebalanceItems: [
      { text: 'Every quarter', value: 1 },
      { text: 'Every other quarter', value: 2 },
      { text: 'Once a year', value: 4 }
    ]
  }),

  computed: {
    ...mapFields({
      backtesterCriteria: 'app.backtesterCriteria'
    }),
    ...mapStoreState('ww', ['quarters'])
  },

  watch: {
    'backtesterCriteria.holdings'(val) {
      this.UpdateSellBufferItems()
    },
    'backtesterCriteria.double_down.percent_increase'(val) {
      if (this.backtesterCriteria.double_down.percent_increase !== null) {
        this.backtesterCriteria.double_down.percent_decline = null
        this.backtesterCriteria.double_down.stdev_decline = null
      }
    },
    'backtesterCriteria.double_down.percent_decline'(val) {
      if (this.backtesterCriteria.double_down.percent_decline !== null) {
        this.backtesterCriteria.double_down.percent_increase = null
        this.backtesterCriteria.double_down.stdev_decline = null
      }
    },
    'backtesterCriteria.double_down.stdev_decline'(val) {
      if (this.backtesterCriteria.double_down.stdev_decline !== null) {
        this.backtesterCriteria.double_down.percent_decline = null
        this.backtesterCriteria.double_down.percent_increase = null
      }
    }
  },
  created() {
    this.UpdateSellBufferItems()
  },
  methods: {
    UpdateSellBufferItems() {
      for (var i = 0; i < this.sellBufferItems.length; i++) {
        var item = this.sellBufferItems[i]
        if (
          item.value > 0 &&
          this.convertHoldingsValue(this.backtesterCriteria.holdings) > item.value
        ) {
          item.disabled = true
        } else {
          item.disabled = false
        }
      }
    },
    convertHoldingsValue(value) {
      var holdings = 0
      switch (value) {
        case 1:
          holdings = 10
          break
        case 2:
          holdings = 20
          break
        case 3:
          holdings = 5
          break
        case 4:
          holdings = 3
          break
        case 5:
          holdings = 1
          break
        case 6:
          holdings = 30
          break
        case 7:
          holdings = 50
          break
        case 8:
          holdings = 2
          break
        case 9:
          holdings = 4
          break
        case 10:
          holdings = 15
          break
        case 11:
          holdings = 6
          break
        case 12:
          holdings = 7
          break
        case 13:
          holdings = 8
          break
        case 14:
          holdings = 9
          break
        case 15:
          holdings = 11
          break
        case 16:
          holdings = 12
          break
        case 17:
          holdings = 13
          break
        case 18:
          holdings = 14
          break
        case 19:
          holdings = 40
          break
        default:
          holdings = 10
      }

      return holdings
    },
    IsSellBufferDisabled(item) {
      if (item.value > 0 && this.backtesterCriteria.holdings < item.value) {
        return true
      }

      return false
    }
  }
}
</script>
